import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@doda/pc/src/redux/hooks';
import { InView, type IntersectionOptions } from 'react-intersection-observer';
import type {
  JobDetailResponse,
  ScoutJobDetailResponse,
} from '@doda/api-client/aspida/@types';
import { BasicModal, Box, Text } from '@doda/io-react/common';
import { loginStatusSelector } from '@doda/common/src/redux/features/memberInfo';
import {
  appQueryParamsSelector,
  jidSelector,
} from '@doda/common/src/redux/features/queryParams';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { getJobOfferKindIsWithoutApplyButton } from '@doda/common/src/logics/jobSearchDetail/getJobOfferKind';
import { createOptimizedContext } from '@doda/common/src/utils/createOptimizedContext';
import { getViewCount } from '@doda/common/src/hooks/useViewCount';
import { useToggle } from 'hooks/useToggle';
import { ModalOverlay } from 'components/ModalOverlay';
import { AnalyticsAppThunk } from '@doda/common/src/types/analytics';
import { RootState } from '@doda/pc/src/redux/store';
import {
  getOfferKindText,
  measureClickApplyModalApply,
  measureImpressionApplyModal,
  measureClickApplyModalFavorite,
} from '../../logics/analytics';
import { useApply } from '../../hooks/useApply';
import type { Tab } from '../../types/tab';
import { ApplyButton } from '../ApplyButton';
import { FavoriteButton } from '../FavoriteButton';
import styles from './index.module.scss';

const [PromoteApplicationModalProvider, useModalState, useSetModal] =
  createOptimizedContext(false);

export { PromoteApplicationModalProvider, useSetModal };

/** Props */
interface PromoteApplicationModalProps {
  jobOfferKind: string;
  jid: string;
  wapid: string;
  isBookmarked: boolean;
  hasDirectLink: boolean;
  directLink: string | undefined;
  isScout: boolean;
  initialTab: Tab;
  job: JobDetailResponse | ScoutJobDetailResponse;
  isCasualInterview?: boolean;
  worschrsvid?: string | null;
}

/** 応募促進モーダル */
export const PromoteApplicationModal = ({
  jobOfferKind,
  jid,
  wapid,
  isBookmarked,
  hasDirectLink,
  directLink,
  isScout,
  initialTab,
  job,
  isCasualInterview,
  worschrsvid,
}: PromoteApplicationModalProps) => {
  const dispatch = useAppDispatch();
  const [isClosed, close] = useToggle(false);
  const isTargetIntersected = useModalState();
  const showModal = !isClosed && isTargetIntersected;
  const { apply } = useApply({
    jid,
    jobOfferKind,
    position: 'top',
    hasDirectLink,
    directLink,
    isScout,
    initialTab,
    isCasualInterview,
    worschrsvid,
  });
  const handleApply = () => {
    dispatch(measureApply({ jid, jobOfferKind }));
    apply();
  };
  const handleBookmark = () => {
    dispatch(measureBookmark({ jid, jobOfferKind }));
  };

  return (
    <>
      {showModal && (
        <PageView
          job={job}
          isScout={isScout}
          isCasualInterview={isCasualInterview}
        />
      )}
      <ModalOverlay noScrollFix show={showModal} onClickOverlay={close}>
        <BasicModal
          show={showModal}
          title="この求人に応募しますか？"
          width={450}
          onClickClose={close}
        >
          <Box
            color="white"
            className={styles['jobSearchDetail-promoteApplicationModal']}
          >
            <Text
              as="p"
              color="black_pc"
              size="14--articlePC"
              weight="normal"
              style={{ marginBlockEnd: 24 }}
            >
              この求人を何度も見ている方へのお知らせです
            </Text>
            <div
              className={
                styles['jobSearchDetail-promoteApplicationModal__buttonGroup']
              }
            >
              <ApplyButton
                size="M"
                jobOfferKind={jobOfferKind}
                onClick={handleApply}
              />
              <FavoriteButton
                size="M"
                withoutBalloon
                jid={jid}
                wapid={wapid}
                isBookmarked={isBookmarked}
                bookmarkListUrl="/dcfront/bookmark/bookmarkList/"
                onClickFavorite={handleBookmark}
                isCasualInterview={isCasualInterview}
              />
            </div>
          </Box>
        </BasicModal>
      </ModalOverlay>
    </>
  );
};

/** モーダル表示判定 */
export function showPromoteApplicationModal(
  job: JobDetailResponse | ScoutJobDetailResponse
) {
  const jobDetail = job.job || {};
  const jobOfferKind = jobDetail.jobOfferKind || '';
  const isBookmarked = Boolean(job.isBookmarkSaved);
  const isApplied = Boolean(job.apply?.isApplied);
  // クライアント側で気になるされた場合は表示される(下記では初期表示時の気になる状態を参照)
  return (
    !getJobOfferKindIsWithoutApplyButton(jobOfferKind) &&
    !isBookmarked &&
    !isApplied
  );
}

/** Props生成 */
export function getPromoteApplicationModalProps(
  job: JobDetailResponse | ScoutJobDetailResponse,
  isScout: boolean,
  initialTab: Tab,
  isCasualInterview?: boolean,
  worschrsvid?: string | null
): PromoteApplicationModalProps {
  const jobDetail = job.job || {};
  return {
    isScout,
    jobOfferKind: jobDetail.jobOfferKind || '',
    jid: jobDetail.jid || '',
    isBookmarked: Boolean(job.isBookmarkSaved),
    wapid: jobDetail.wapid || '',
    hasDirectLink: Boolean(jobDetail.hasDirectLink),
    directLink: jobDetail.directLink || undefined,
    initialTab,
    job,
    isCasualInterview,
    worschrsvid,
  };
}

/** PV計測用コンポーネント */
const PageView = ({
  job,
  isScout,
  isCasualInterview,
}: {
  job: JobDetailResponse | ScoutJobDetailResponse;
  isScout: boolean;
  isCasualInterview?: boolean;
}) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(measureImpression({ job, isScout, isCasualInterview }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

/** PV計測 */
function measureImpression({
  job,
  isScout,
  isCasualInterview,
}: {
  job: JobDetailResponse | ScoutJobDetailResponse;
  isScout: boolean;
  isCasualInterview?: boolean;
}): AnalyticsAppThunk<RootState> {
  return (_, getState) => {
    const appQuery = appQueryParamsSelector(getState());
    const jobOfferKind = job.job?.jobOfferKind || '';
    const applyText = getOfferKindText(jobOfferKind);
    measureImpressionApplyModal(
      job,
      appQuery,
      isScout,
      applyText,
      isCasualInterview
    );
  };
}

/** 応募計測 */
function measureApply({
  jid,
  jobOfferKind,
}: {
  jid: string;
  jobOfferKind: string;
}): AnalyticsAppThunk<RootState> {
  return (dispatch, getState) => {
    const isAdobeLoaded = adobeAnalyticsScriptsSelector(getState());
    const applyText = getOfferKindText(jobOfferKind);
    measureClickApplyModalApply(isAdobeLoaded, dispatch, applyText, jid);
  };
}

/** 気になる計測 */
function measureBookmark({
  jid,
  jobOfferKind,
}: {
  jid: string;
  jobOfferKind: string;
}): AnalyticsAppThunk<RootState> {
  return (dispatch, getState) => {
    const isAdobeLoaded = adobeAnalyticsScriptsSelector(getState());
    const applyText = getOfferKindText(jobOfferKind);
    measureClickApplyModalFavorite(isAdobeLoaded, dispatch, applyText, jid);
  };
}

/** ターゲットエリアProps */
interface PromoteApplicationModalDisplayTargetProps {
  children: React.ReactNode;
  /** ターゲットが表示エリアに入った際の追加処理(計測など) */
  onVisible?: VoidFunction;
  isScout: boolean;
  isRead: boolean;
}

/** 応募促進モーダル表示ターゲットエリア */
export const PromoteApplicationModalDisplayTarget = ({
  children,
  onVisible,
  isScout,
  isRead,
}: PromoteApplicationModalDisplayTargetProps) => {
  const isOpen = useModalState();
  const setModal = useSetModal();
  const jid = useAppSelector(jidSelector);
  const isLogin = useAppSelector(loginStatusSelector);

  // 一度開いたら監視不要のためInViewコンポーネントはアンマウントする
  if (isOpen) return children;

  const handleViewChange: IntersectionOptions['onChange'] = (inView) => {
    if (!inView) return;
    const viewCountList = getViewCount();
    const viewCount = viewCountList.find((item) => item.wjoid === jid);
    // ログイン && 閲覧済み && 通常原稿
    // 上記を満たす場合はviewCountに関わらずモーダル表示
    const showModal =
      (viewCount && viewCount.count >= 2) || (isLogin && isRead && !isScout);
    if (!showModal) return;
    setModal(true);
    onVisible?.();
  };

  return (
    <InView as="div" onChange={handleViewChange}>
      {children}
    </InView>
  );
};
